import React, { useState, useEffect } from 'react';
import Sidebar from '../components/sidebar';
import { Card, Upload, Button, Table, message, Space, Typography, Tag, Modal, Alert } from 'antd';
import { UploadOutlined, DeleteOutlined, FileTextOutlined, FilePdfOutlined, CheckCircleOutlined } from '@ant-design/icons';
import axios from 'axios';
import './ActualizarIA.css';

const { Title, Text } = Typography;
const { Dragger } = Upload;

const ActualizarIA = () => {
  const [fileList, setFileList] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(true);
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    const loginUserJSON = window.localStorage.getItem("loginUser");
    if (loginUserJSON) {
      const user = JSON.parse(loginUserJSON);
      setUserData(user);
      // Verificar si es admin
      if (user.rol !== 'admin' && user.rol !== 'administrador') {
        message.error('No tienes permisos para acceder a esta página');
        window.location.href = '/chat-ia';
      }
    }
    cargarArchivos();
  }, []);

  const cargarArchivos = async () => {
    try {
      setLoadingFiles(true);
      console.log('Cargando archivos desde el servidor...');
      
      const response = await axios.get('https://api.mrcirp.com/public/archivos-ia');
      
      console.log('Respuesta completa del servidor:', response);
      console.log('Datos recibidos:', response.data);
      
      // Asegurar que siempre sea un array
      let data = response.data;
      
      // Si la respuesta es un objeto con propiedad data, usar esa
      if (data && typeof data === 'object' && !Array.isArray(data) && data.data) {
        data = data.data;
      }
      
      // Verificar que sea un array
      if (Array.isArray(data)) {
        console.log(`Se encontraron ${data.length} archivo(s)`);
        setUploadedFiles(data);
        
        if (data.length === 0) {
          console.log('No hay archivos en la base de datos');
        }
      } else {
        console.warn('Formato de respuesta inesperado, se esperaba un array:', data);
        console.warn('Tipo de dato recibido:', typeof data);
        setUploadedFiles([]);
      }
    } catch (error) {
      console.error('Error al cargar archivos:', error);
      console.error('Detalles del error:', error.response?.data || error.message);
      console.error('Status del error:', error.response?.status);
      
      // Si es un error 404 o la tabla no existe, mostrar mensaje amigable
      if (error.response && error.response.status === 404) {
        console.log('La tabla de archivos aún no existe o está vacía');
        setUploadedFiles([]);
      } else {
        message.error('Error al cargar los archivos existentes');
        setUploadedFiles([]);
      }
    } finally {
      setLoadingFiles(false);
    }
  };

  const validateFile = (file) => {
    const isValidType = file.type === 'application/pdf' || 
                       file.type === 'application/msword' || 
                       file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    
    if (!isValidType) {
      message.error('Solo se permiten archivos PDF o DOC/DOCX');
      return false;
    }

    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      message.error('El archivo no debe exceder 10MB');
      return false;
    }

    return true;
  };

  const handleUpload = async () => {
    if (fileList.length === 0) {
      message.warning('Por favor selecciona al menos un archivo');
      return;
    }

    setLoading(true);
    
    try {
      for (const file of fileList) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('nombre', file.name);
        formData.append('tipo', file.type);
        formData.append('tamaño', file.size);

        console.log('Subiendo archivo:', file.name, 'Tipo:', file.type, 'Tamaño:', file.size);
        
        const response = await axios.post('https://api.mrcirp.com/public/upload-archivo-ia', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        
        console.log('Respuesta del servidor:', response.data);
        
        if (response.data && response.data.success === false) {
          message.error(response.data.message || 'Error al subir el archivo');
          return;
        }
      }

      message.success(`Se subieron ${fileList.length} archivo(s) exitosamente`);
      setFileList([]);
      
      // Esperar un momento antes de recargar para asegurar que el servidor procesó todo
      setTimeout(() => {
        cargarArchivos();
      }, 500);
    } catch (error) {
      console.error('Error al subir archivos:', error);
      console.error('Detalles del error:', error.response?.data || error.message);
      
      if (error.response?.data?.message) {
        message.error(`Error: ${error.response.data.message}`);
      } else {
        message.error('Error al subir los archivos. Revisa la consola para más detalles.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: '¿Estás seguro de que deseas eliminar este archivo?',
      content: 'Esta acción no se puede deshacer',
      okText: 'Eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await axios.delete(`https://api.mrcirp.com/public/archivos-ia/${id}`);
          message.success('Archivo eliminado exitosamente');
          cargarArchivos();
        } catch (error) {
          console.error('Error al eliminar archivo:', error);
          message.error('Error al eliminar el archivo');
        }
      },
    });
  };

  const props = {
    onRemove: (file) => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
    },
    beforeUpload: (file) => {
      if (validateFile(file)) {
        setFileList([...fileList, file]);
      }
      return false; // Prevenir subida automática
    },
    fileList,
    accept: '.pdf,.doc,.docx',
  };

  const columns = [
    {
      title: 'Nombre del Archivo',
      dataIndex: 'nombre',
      key: 'nombre',
      render: (text, record) => (
        <Space>
          {record.tipo === 'application/pdf' ? (
            <FilePdfOutlined style={{ fontSize: '20px', color: '#ff4d4f' }} />
          ) : (
            <FileTextOutlined style={{ fontSize: '20px', color: '#1890ff' }} />
          )}
          <Text>{text}</Text>
        </Space>
      ),
    },
    {
      title: 'Tipo',
      dataIndex: 'tipo',
      key: 'tipo',
      render: (tipo) => (
        <Tag color={tipo === 'application/pdf' ? 'red' : 'blue'}>
          {tipo === 'application/pdf' ? 'PDF' : 'DOC/DOCX'}
        </Tag>
      ),
    },
    {
      title: 'Tamaño',
      dataIndex: 'tamaño',
      key: 'tamaño',
      render: (tamaño) => {
        const sizeInMB = (tamaño / (1024 * 1024)).toFixed(2);
        return `${sizeInMB} MB`;
      },
    },
    {
      title: 'Fecha de Subida',
      dataIndex: 'fecha_subida',
      key: 'fecha_subida',
      render: (fecha) => new Date(fecha).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }),
    },
    {
      title: 'Acciones',
      key: 'acciones',
      render: (_, record) => (
        <Button
          type="link"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleDelete(record.id)}
        >
          Eliminar
        </Button>
      ),
    },
  ];

  return (
    <Sidebar>
      <div className="actualizar-ia-container">
        <Card>
          <Title level={2} style={{ marginBottom: '8px' }}>Actualizar IA</Title>
          <Text type="secondary" style={{ display: 'block', marginBottom: '24px' }}>
            Sube documentos PDF o DOC/DOCX para entrenar y actualizar la inteligencia artificial.
          </Text>

          <div className="upload-section">
            <div className="upload-dragger-wrapper">
              <Dragger {...props}>
                <p className="ant-upload-drag-icon">
                  <UploadOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
                </p>
                <p className="ant-upload-text">Haz clic o arrastra archivos aquí para subirlos</p>
                <p className="ant-upload-hint">
                  Solo se permiten archivos PDF, DOC o DOCX. Tamaño máximo: 10MB por archivo.
                </p>
              </Dragger>
            </div>

            {fileList.length > 0 && (
              <div style={{ marginTop: '16px' }}>
                <Text strong>Archivos seleccionados ({fileList.length}):</Text>
                <ul style={{ marginTop: '8px', marginBottom: '16px', paddingLeft: '20px' }}>
                  {fileList.map((file, index) => (
                    <li key={index} style={{ marginBottom: '4px', wordBreak: 'break-word' }}>
                      {file.name} ({(file.size / (1024 * 1024)).toFixed(2)} MB)
                    </li>
                  ))}
                </ul>
                <Button
                  type="primary"
                  icon={<UploadOutlined />}
                  onClick={handleUpload}
                  loading={loading}
                  size="large"
                >
                  Subir Archivos
                </Button>
              </div>
            )}
          </div>

          <div className="files-section">
            <Title level={3} style={{ marginBottom: '16px' }}>Archivos Subidos</Title>
            <div style={{ overflowX: 'auto' }}>
              <Table
                columns={columns}
                dataSource={Array.isArray(uploadedFiles) ? uploadedFiles : []}
                rowKey="id"
                loading={loadingFiles}
                scroll={{ x: 'max-content' }}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  showTotal: (total) => `Total: ${total} archivo(s)`,
                  showQuickJumper: true,
                }}
              />
            </div>
          </div>
        </Card>
      </div>
    </Sidebar>
  );
};

export default ActualizarIA;

